import { useTranslation } from 'react-i18next';
import { ReactionArrowGlyph } from '../ReactionArrowGlyph';
import { Icon } from '../Icon';
import { SCHEME_ENTRIES } from '../../app/workspace/ReactionArrowsGroup';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';

/**
 * #698 -- the selected reaction arrow's type, reassignable at any time (the rail's own glyphs, the
 * current one pressed), and a Reverse action that flips it (reactants <-> products by position).
 * Shown in the Edit tab while a reaction arrow is selected.
 */
export function ReactionArrowSection({ doc }: { readonly doc: UseDrawingDocumentResult }) {
  const { t } = useTranslation();
  const arrow = doc.document.annotations.find(
    (annotation) => annotation.kind === 'reaction-arrow' && doc.selection.annotationIds?.includes(annotation.id)
  );
  if (!arrow || arrow.kind !== 'reaction-arrow') return null;

  return (
    <section
      className="rail-section"
      aria-labelledby="reaction-arrow-section-heading"
      data-testid="reaction-arrow-section"
    >
      <h2 id="reaction-arrow-section-heading">{t('reactionArrowSection.heading')}</h2>
      <div className="workspace-rail-annotations-grid workspace-rail-annotations-grid--triple">
        {SCHEME_ENTRIES.map(({ toolId, kind, overlay, labelKey }) => {
          const current = arrow.arrowKind === kind && (arrow.overlay ?? undefined) === overlay;
          return (
            <button
              key={toolId}
              type="button"
              className={`icon-button ${current ? 'active' : ''}`}
              aria-label={t(labelKey)}
              title={t(labelKey)}
              aria-pressed={current}
              onClick={() => doc.setReactionArrowKind(arrow.id, kind, overlay)}
            >
              <ReactionArrowGlyph kind={kind} {...(overlay ? { overlay } : {})} />
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="icon-button reaction-arrow-reverse"
        data-testid="reaction-arrow-reverse"
        aria-label={t('reactionArrowSection.reverse')}
        title={t('reactionArrowSection.reverseHint')}
        onClick={() => doc.reverseReactionArrow(arrow.id)}
      >
        <Icon name="swapHoriz" />
      </button>
    </section>
  );
}
